<template>
    <form class="f fv c-el-panel-bg ath-global-modal-el-standard"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="(uiTitle || uiTitleEntity) ? titleId : null"
        @submit.prevent="actSubmit">
        <header class="f fh fa-c fj-l sp-l-m sp-t-m sp-r-m sp-b-s el-modal-header" :class="uiHeaderClass">
            <slot name="titleBefore"></slot>
            <h2 class="el-modal-title" :id="titleId" v-if="uiTitle || uiTitleEntity">
                <span v-if="uiTitle && !uiTitleEntity">
                    {{ uiTitle | athT(uiTitleParams) }}
                </span>
                <span v-if="uiTitle && uiTitleEntity">
                    {{ uiTitle | athTEnt(uiTitleEntity, uiTitleEntityLc) }}
                </span>
                <slot name="title"></slot>
            </h2>
            <slot name="titleAfter"></slot>
            <span class="fg"></span>
            <slot name="titleAction"></slot>
            <ButtonIcon icon="cross" title="mead.labels.close" :disabled="uiDisabled" class="fas-l" v-if="uiClose" @click="close" />
        </header>
        <slot name="nav"></slot>
        <main class="f fv sp-m el-modal-body" :class="uiBodyClass" v-if="uiBody">
            <slot name="body"></slot>
        </main>
        <footer class="f fh sp-m el-modal-footer" :class="uiFooterClass" v-if="uiFooter">
            <slot name="footer"></slot>
            <MeadButton v-if="uiCancel"
                :lbl="'mead.labels.cancel'"
                class="el--s-m el--t-default"
                :disabled="uiDisabled"
                :qa="'cancel-button'"
                @click="close" />
        </footer>
    </form>
</template>

<script>
import { guuid } from '@aretheyhappy/utils';
import Button from '../../atoms/Button.vue';
import ButtonIcon from '../../atoms/ButtonIcon.vue';

export default {
    name: 'mead-standard-modal',
    components: {
        'MeadButton': Button,
        ButtonIcon
    },
    data () {
        return {
            titleId: `mead-modal-title-${guuid()}`,
        };
    },
    props: {
        /**
         * Title text or localization key shown in the header. The header title
         * is only rendered when this or uiTitleEntity is set.
         */
        uiTitle         : {type: [Boolean, String], default: () => false},
        /**
         * Interpolation parameters passed to the athT translation filter when
         * rendering uiTitle.
         */
        uiTitleParams   : {type: Object, default: () => ({})},
        /**
         * Entity name used to translate uiTitle via the athTEnt filter instead of
         * athT, for entity-specific title phrasing. False disables entity translation.
         */
        uiTitleEntity   : {type: [Boolean, String], default: () => false},
        /**
         * Whether uiTitleEntity should be lowercased when interpolated by athTEnt.
         */
        uiTitleEntityLc : {type: Boolean, default: () => false},
        /**
         * Whether the modal is in a disabled state, disabling the header close
         * button and the footer cancel button (e.g. while a submit is in progress).
         */
        uiDisabled      : {type: Boolean, default: () => false},
        /**
         * Additional CSS class(es) applied to the header element.
         */
        uiHeaderClass   : {type: String, default: () => ''},
        /**
         * Whether to render the body/main section (and its body slot).
         */
        uiBody          : {type: Boolean, default: () => true},
        /**
         * Additional CSS class(es) applied to the body/main element.
         */
        uiBodyClass     : {type: String, default: () => ''},
        /**
         * Whether to render the footer section (and its footer slot).
         */
        uiFooter        : {type: Boolean, default: () => true},
        /**
         * Additional CSS class(es) applied to the footer element.
         */
        uiFooterClass   : {type: String, default: () => ''},
        /**
         * Whether to show the default Cancel button in the footer.
         */
        uiCancel        : {type: Boolean, default: () => true},
        /**
         * Whether to show the close (X) button in the header.
         */
        uiClose         : {type: Boolean, default: () => true},
        /**
         * Width of the modal, in rem units, applied directly to the root element's
         * style. False leaves the width unset.
         */
        uiWidth         : {type: [Boolean, Number, String], default: () => 45},
        /**
         * Height of the modal, in rem units, applied directly to the root element's
         * style. False leaves the height to size naturally with its content.
         */
        uiHeight        : {type: [Boolean, Number, String], default: () => false},
        /**
         * Whether this modal is managed by the global ModalService (closing it calls
         * ModalService.close()) versus being used standalone, in which case close()
         * instead emits a local 'close' event for the parent to handle.
         */
        uiIsGlobal      : {type: Boolean, default: true},
    },
    watch: {
        uiWidth: function () {
            this.setWidth();
        },
        uiHeight: function () {
            this.setHeight();
        },
    },
    methods: {
        setWidth: function () {
            if (!this.$el || !this.uiWidth) return;
            this.$el.style.width = `${this.uiWidth}rem`;
        },
        setHeight: function () {
            if (!this.$el || !this.uiHeight) return;
            this.$el.style.height = `${this.uiHeight}rem`;
        },
        actSubmit: function () {
            this.$emit('actSubmit');
        },
        close: function (event) {
            if (this.uiIsGlobal) MeadCommon.ModalService.close();
            else this.$emit('close', event);
        },
    },
    mounted () {
        this.setWidth();
        this.setHeight();
    },
};
</script>
<style lang="scss" scoped>
@use '../../../../sass/variables' as *;

.ath-global-modal-el-standard {
    .el-modal-title {
        display: flex;
        align-items: center;
        line-height: unset;

        .el-help-lnk {
            transform: translateY(.2rem);
        }
    }

    p:not([class*="te-"]) {
        font-size: $fs-m;
    }

    .ath-loader {
        background-color: rgba(255, 255, 255, .8);
        margin-top: 0;
    }

    @media screen and (max-width: $mq-ds) {
        .el-modal-footer {
            flex-direction: column;
            gap: $s-s;

            > * {
                width: 100%;
            }
        }
    }

    @media screen and (min-width: $mq-ds-break) {
        .ath-loader {
            border-radius: $br-m;
        }

        .el-modal-footer {
            flex-direction: row;
            gap: $s-s;

            &:not(.el--btn-fng) > * {
                flex-grow: 1;
            }

            > * {
                width: auto;
            }
        }
    }
}

</style>